import ScratchJr from '../../../ScratchJr';
import ActivityStorageService from '../../storage/ActivityStorageService';
import ActivityAudio from '../../shared/ActivityAudio';
import CompletionModal from '../../shared/CompletionModal';
import {scorePreferredNarrationVoice} from '../../shared/SpeechVoicePreference';
import PaletteRestrictor from '../guided-creation/PaletteRestrictor';
import {gn} from '../../../../utils/lib';
import {evaluateSuccessCriteria} from '../../../../../../web/ai/activity-success';

export const PHASES = Object.freeze({
    INTRO: 'INTRO',
    CREATIVE: 'CREATIVE',
    COMPLETE: 'COMPLETE'
});

export default class MyStorybookFlow {
    constructor (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._config = activityData.myStorybook || {};
        this._phase = PHASES.INTRO;
        this._paletteRestrictor = null;
        this._doneBtn = null;
        this._destroyed = false;
        this._projectHasRun = false;
        this._flagTapCount = 0;
        this._pageVoPlayed = {};
        this._lastPageIndex = -1;
        this._pageMonitor = null;
        this._speechVoice = null;
        this._speechQueue = Promise.resolve();
        this._voicesChangeHandler = this._refreshPreferredVoice.bind(this);
    }

    async init () {
        // Palette
        const blocks = (this._config.availableBlocks && this._config.availableBlocks[PHASES.CREATIVE])
            || this._activityData.scaffolding.availableBlocks || [];
        this._paletteRestrictor = new PaletteRestrictor(this._container, blocks);

        // Voice
        this._initVoiceSelection();
        await this._waitForPreferredVoice();

        // Page voiceover guards
        const vo = this._config.voiceOver || {};
        for (const key of Object.keys(vo)) {
            const match = key.match(/^VO-PAGE(\d+)$/);
            if (match) {
                this._pageVoPlayed[parseInt(match[1], 10)] = false;
            }
        }

        // Restore progress
        const saved = await ActivityStorageService.getActivityProgress(this._activityData.activityId);
        if (saved) {
            if (saved.pageVoPlayed) {
                for (const k of Object.keys(saved.pageVoPlayed)) {
                    const n = parseInt(k, 10);
                    if (n in this._pageVoPlayed) {
                        this._pageVoPlayed[n] = saved.pageVoPlayed[k];
                    }
                }
            }
            if (saved.projectHasRun) {
                this._projectHasRun = true;
            }
            if (typeof saved.flagTapCount === 'number') {
                this._flagTapCount = saved.flagTapCount;
            }
        }

        const savedPhase = saved && saved.currentState && PHASES[saved.currentState]
            ? saved.currentState : null;

        if (savedPhase && savedPhase !== PHASES.INTRO) {
            this._phase = savedPhase === PHASES.COMPLETE ? PHASES.CREATIVE : savedPhase;
            this._showDoneButtonIfEligible();
            this._startPageMonitor();
            this._speak('VO-RESUME', 'Welcome back! Keep adding to your storybook!');
            await this._persistProgress();
            return;
        }

        // Fresh start
        this._phase = PHASES.INTRO;
        this._startPageMonitor();

        const introDelay = this._config.introDelayMs || 4200;
        setTimeout(() => {
            if (this._destroyed) return;
            this._speak('VO-INTRO', null, {
                onend: () => {
                    if (!this._destroyed) {
                        this._phase = PHASES.CREATIVE;
                        this._persistProgress();
                    }
                }
            });
        }, introDelay);

        await this._persistProgress();
    }

    onStateChange (prevState, newState) {
        if (newState === 'RUNNING') {
            this._flagTapCount++;
            this._projectHasRun = true;
            this._persistProgress();
            return;
        }

        if (newState === 'VALIDATING') {
            this._stateMachine.transition('BUILDING');
            this._showDoneButtonIfEligible();
            this._persistProgress();
            return;
        }
    }

    evaluate () {
        const simState = this._buildSimState();
        return evaluateSuccessCriteria(this._activityData, simState);
    }

    _buildSimState () {
        return {
            pagesWithScripts: this._countPagesWithScripts(),
            projectHasRun: this._projectHasRun,
            phaseFlags: {
                completed: this._phase === PHASES.COMPLETE,
                storyPhase: this._phase,
                flagTapCount: this._flagTapCount
            }
        };
    }

    getBlockCount () {
        return ScratchJr.collectActivityBlocks ? ScratchJr.collectActivityBlocks().length : 0;
    }

    showHint () {}

    captureRunStartPosition () {}

    shouldResetSpritesBeforeRun () {
        return true;
    }

    shouldEvaluateWhileThreadsRunning () {
        return false;
    }

    restartActivity () {
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }
        this._projectHasRun = false;
        this._flagTapCount = 0;
        this._hideDoneButton();
        for (const k of Object.keys(this._pageVoPlayed)) {
            this._pageVoPlayed[k] = false;
        }
        this._phase = PHASES.CREATIVE;
        this._persistProgress();
        this._speak('VO-INTRO');
        return true;
    }

    destroy () {
        this._destroyed = true;
        this._stopPageMonitor();
        this._stopNarration();
        this._hideDoneButton();
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
        }
        if (window.speechSynthesis && typeof window.speechSynthesis.onvoiceschanged !== 'undefined') {
            window.speechSynthesis.onvoiceschanged = null;
        }
    }

    // --- Done button ---

    _showDoneButtonIfEligible () {
        const pagesWithScripts = this._countPagesWithScripts();
        const minRequired = (this._activityData.successCriteria && this._activityData.successCriteria.minPagesWithScripts) || 2;
        if (pagesWithScripts >= minRequired && this._projectHasRun) {
            this._showDoneButton();
        }
    }

    _showDoneButton () {
        if (this._doneBtn) return;
        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-my-storybook-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => this._enterComplete());
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    // --- Completion ---

    _enterComplete () {
        this._phase = PHASES.COMPLETE;
        this._hideDoneButton();
        ActivityAudio.play('victory');
        this._speak('VO-COMPLETE', null, {
            onend: () => {
                if (this._destroyed) return;
                this._persistProgress({status: 'completed', completedAt: new Date().toISOString()});
                CompletionModal.show({
                    title: 'My Storybook',
                    message: 'You made a whole storybook! You are a real storyteller!',
                    onReplay: () => this.restartActivity()
                });
            }
        });
    }

    // --- Page monitor ---

    _startPageMonitor () {
        this._stopPageMonitor();
        this._pageMonitor = setInterval(() => {
            if (!ScratchJr.stage) return;
            const index = ScratchJr.stage.pages.indexOf(ScratchJr.stage.currentPage);
            if (index !== this._lastPageIndex) {
                this._lastPageIndex = index;
                this._onPageChanged(index);
            }
        }, 400);
    }

    _stopPageMonitor () {
        if (this._pageMonitor) {
            clearInterval(this._pageMonitor);
            this._pageMonitor = null;
        }
    }

    _onPageChanged (pageIndex) {
        const pageNum = pageIndex + 1;
        if (this._pageVoPlayed[pageNum] === false) {
            this._pageVoPlayed[pageNum] = true;
            this._speak('VO-PAGE' + pageNum);
            this._persistProgress();
        }
    }

    // --- Narration ---

    _speak (voiceId, fallbackText, opts) {
        opts = opts || {};
        const vo = this._config.voiceOver || this._activityData.voiceOver || {};
        const text = vo[voiceId] || fallbackText;
        if (!text) {
            if (opts.onend) opts.onend();
            return;
        }

        if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            if (opts.onend) opts.onend();
            return;
        }

        try {
            window.speechSynthesis.cancel();
            var utterance = new SpeechSynthesisUtterance(text);
            var voice = this._speechVoice;
            utterance.lang = voice && voice.lang ? voice.lang : 'en-US';
            if (voice) utterance.voice = voice;
            utterance.rate = 0.85;
            utterance.pitch = 1.05;
            utterance.volume = 1;
            utterance.onend = function () { if (opts.onend) opts.onend(); };
            utterance.onerror = function () { if (opts.onend) opts.onend(); };
            window.speechSynthesis.speak(utterance);
        } catch (_) {
            if (opts.onend) opts.onend();
        }
    }

    _stopNarration () {
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            try { window.speechSynthesis.cancel(); } catch (_) {}
        }
    }

    _initVoiceSelection () {
        this._refreshPreferredVoice();
        if (window.speechSynthesis && typeof window.speechSynthesis.onvoiceschanged !== 'undefined') {
            window.speechSynthesis.onvoiceschanged = this._voicesChangeHandler;
        }
    }

    _waitForPreferredVoice () {
        return new Promise((resolve) => {
            if (this._speechVoice) { resolve(); return; }
            var attempts = 0;
            var check = () => {
                this._refreshPreferredVoice();
                if (this._speechVoice || attempts >= 10) { resolve(); return; }
                attempts++;
                setTimeout(check, 200);
            };
            check();
        });
    }

    _refreshPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') return;
        var voices = window.speechSynthesis.getVoices();
        if (!voices || voices.length === 0) return;
        var best = null;
        var bestScore = -Infinity;
        for (var i = 0; i < voices.length; i++) {
            var score = scorePreferredNarrationVoice(voices[i], window);
            if (score > bestScore) { bestScore = score; best = voices[i]; }
        }
        if (best) this._speechVoice = best;
    }

    // --- Progress ---

    async _persistProgress (overrides) {
        overrides = overrides || {};
        await ActivityStorageService.saveProgress(this._activityData.activityId, {
            status: overrides.status || (this._phase === PHASES.COMPLETE ? 'completed' : 'in_progress'),
            currentState: this._phase,
            pageVoPlayed: {...this._pageVoPlayed},
            projectHasRun: this._projectHasRun,
            flagTapCount: this._flagTapCount,
            lastPageIndex: this._lastPageIndex,
            completedAt: overrides.completedAt,
            lastOpenedAt: new Date().toISOString()
        });
    }

    // --- Helpers ---

    _countPagesWithScripts () {
        if (!ScratchJr.stage || !ScratchJr.stage.pages) return 0;
        var count = 0;
        for (var i = 0; i < ScratchJr.stage.pages.length; i++) {
            var page = ScratchJr.stage.pages[i];
            if (!page || typeof page.getSprites !== 'function') continue;
            var spriteIds = page.getSprites();
            var pageHasScripts = false;
            for (var j = 0; j < spriteIds.length; j++) {
                var el = gn(spriteIds[j]);
                if (!el || !el.owner || el.owner.type !== 'sprite') continue;
                // Use getData().scripts which encodes actual block strips
                var data = el.owner.getData();
                if (data.scripts && data.scripts.length > 0) {
                    pageHasScripts = true;
                    break;
                }
            }
            if (pageHasScripts) count++;
        }
        return count;
    }
}
